import type { BuildStore } from '../types';
import { ifcFromSite, toStoreyLocal, type Point2, type Point3 } from './frame';
import { elevationOf, sourceStorey } from './storeys';
import type { BuildParams, BuildResult } from './types';

const THICKNESS = 0.2;
const HEIGHT = 2.8;
const SLAB_THICKNESS = 0.2;

// One wall between two points the caller gives in site metres.
export async function addWall(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  if (!params.start || !params.end) throw new Error('add_wall needs start and end');
  const storey = await sourceStorey(store, params.storey);
  const elevation = elevationOf(storey);
  const start = toStoreyLocal(params.start, elevation);
  const end = toStoreyLocal(params.end, elevation);
  if (distance(start, end) < 0.01) throw new Error('add_wall needs two different points');
  const id = await store.addWall(storey.expressID, {
    Start: [start[0], start[1], 0],
    End: [end[0], end[1], 0],
    Thickness: params.thickness ?? THICKNESS,
    Height: params.height ?? HEIGHT,
    Name: params.name ?? `${storey.name} wall`,
  });
  return {
    created: [id],
    removed: [],
    note: `${distance(start, end).toFixed(2)} m wall on ${storey.name}`,
  };
}

// One slab under a footprint the caller gives as a site ground plan, x across and z into it.
export async function addSlab(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  if (!params.footprint || params.footprint.length < 3)
    throw new Error('add_slab needs a footprint of at least three points');
  const storey = await sourceStorey(store, params.storey);
  const elevation = elevationOf(storey);
  const thickness = params.thickness ?? SLAB_THICKNESS;
  const top = params.at != null ? params.at - elevation : 0;
  const id = await store.addSlab(storey.expressID, {
    Profile: 'polygon',
    OuterCurve: params.footprint.map(plan),
    Position: [0, 0, top - thickness],
    Thickness: thickness,
    Name: params.name ?? `${storey.name} slab`,
  });
  return {
    created: [id],
    removed: [],
    note: `slab over ${params.footprint.length} points on ${storey.name}`,
  };
}

const plan = (p: Point2): Point2 => {
  const [x, y] = ifcFromSite([p[0], 0, p[1]]);
  return [x, y];
};

const distance = (a: Point3, b: Point3) => Math.hypot(b[0] - a[0], b[1] - a[1]);
